<template>
    <div class="conflict-list">
      <div v-if="!conflicts || conflicts.length === 0" class="text-center py-3">
        <p class="text-muted">{{ $t('No issues found in this category.') }}</p>
      </div>
      
      <b-list-group v-else>
        <b-list-group-item
          v-for="conflict in filteredConflicts"
          :key="conflict.id"
          class="conflict-item"
          :class="{ 'resolved': conflict.status === 'resolved' }"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div>
              <h5 class="mb-1">{{ conflict.description }}</h5>
              
              <p v-if="conflict.status === 'resolved'" class="text-success mb-2">
                <font-awesome-icon :icon="['fa', 'check-circle']" /> 
                {{ $t('Resolved') }}
              </p>
              
              <ul v-if="conflict.details && conflict.details.length" class="pl-3 mb-2">
                <li v-for="(detail, index) in conflict.details" :key="index">
                  {{ detail }}
                </li>
              </ul>
              
              <div v-if="conflict.resolution" class="resolution-notes p-2 mt-2 mb-2 border-left border-success">
                <small class="text-muted">{{ $t('Resolution Notes:') }}</small>
                <p class="mb-0">{{ conflict.resolution }}</p>
              </div>
            </div>
            
            <div>
              <b-badge
                :variant="getConflictVariant(conflict)"
                class="mb-2"
              >
                {{ getConflictType() }}
              </b-badge>
              
              <div v-if="conflict.status !== 'resolved'">
                <b-button
                  size="sm"
                  variant="outline-success"
                  @click="$emit('resolve', conflict)"
                >
                  <font-awesome-icon :icon="['fa', 'check']" />
                  {{ $t('Resolve') }}
                </b-button>
              </div>
            </div>
          </div>
        </b-list-group-item>
      </b-list-group>
      
      <div class="d-flex justify-content-between mt-3">
        <div>
          <b-form-checkbox v-model="showResolved" switch>
            {{ $t('Show resolved') }}
          </b-form-checkbox>
        </div>
        
        <div v-if="conflicts.length > 0">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="exportConflicts"
          >
            <font-awesome-icon :icon="['fa', 'download']" />
            {{ $t('Export') }}
          </b-button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'ConflictList',
    props: {
      conflicts: {
        type: Array,
        default: () => []
      },
      type: {
        type: String,
        default: 'general'
      }
    },
    data() {
      return {
        showResolved: false
      };
    },
    computed: {
      filteredConflicts() {
        if (this.showResolved) {
          return this.conflicts;
        }
        return this.conflicts.filter(conflict => conflict.status !== 'resolved');
      }
    },
    methods: {
        $t(text) {
      // Simple passthrough function until proper i18n is set up
      return text;
    },
      getConflictVariant() {
        const variantMap = {
          'critical': 'danger',
          'sequencing': 'warning',
          'spatial': 'info',
          'inspection': 'primary',
          'resource': 'secondary'
        };
        
        return variantMap[this.type] || 'secondary';
      },
      
      getConflictType() {
        const typeMap = {
          'critical': this.$t('Critical'),
          'sequencing': this.$t('Sequencing'),
          'spatial': this.$t('Spatial'),
          'inspection': this.$t('Inspection'),
          'resource': this.$t('Resource')
        };
        
        return typeMap[this.type] || this.$t('General');
      },
      
      exportConflicts() {
      // Generate CSV or PDF export of conflicts
      const conflictData = this.conflicts.map(conflict => ({
        Type: this.getConflictType(),
        Description: conflict.description,
        Details: conflict.details ? conflict.details.join('; ') : '',
        Status: conflict.status === 'resolved' ? this.$t('Resolved') : this.$t('Open'),
        Resolution: conflict.resolution || ''
      }));
      
      // Convert to CSV
      const headers = Object.keys(conflictData[0]);
      const csvContent = [
        headers.join(','),
        ...conflictData.map(row => headers.map(header => {
          // Escape quotes and wrap in quotes
          const cell = row[header].toString();
          return `"${cell.replace(/"/g, '""')}"`;
        }).join(','))
      ].join('\n');
      
      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${this.getConflictType()}_conflicts.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
.conflict-item {
  transition: background-color 0.3s ease;
}

.conflict-item.resolved {
  background-color: rgba(40, 167, 69, 0.1);
}

.resolution-notes {
  border-left-width: 4px !important;
  background-color: rgba(40, 167, 69, 0.05);
}
</style>